/**
 * Docling Text Extractor
 * Handles text extraction from various docling document formats
 */

import { logger } from '../core/logger';

export class DoclingTextExtractor {
  /**
   * Extract plain text from docling result
   * @param {any} document - Docling document object from API response
   * @returns {string} Extracted text content
   */
  extractTextFromDoclingResult(document: any): string {
    if (!document) return '';

    // Priority order: json_content > text_content > md_content
    // JSON provides structured data for better text extraction
    if (document.json_content) {
      return this.extractTextFromJsonContent(document.json_content);
    }

    // text_content is already plain text, no need to strip markdown
    if (document.text_content) {
      return document.text_content;
    }

    // If markdown is available, convert to plain text
    if (document.md_content) {
      return this.markdownToPlainText(document.md_content);
    }

    // Fallback: try older response formats
    if (document.markdown) {
      return this.markdownToPlainText(document.markdown);
    }

    if (document.text) {
      return document.text;
    }

    return '';
  }

  /**
   * Convert markdown to plain text
   * @param {string} markdown - Markdown content
   * @returns {string} Plain text
   */
  markdownToPlainText(markdown: string): string {
    return markdown
      .replace(/^#{1,6}\s+/gm, '') // Remove headers
      .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.+?)\*/g, '$1') // Remove italic
      .replace(/\[(.+?)\]\(.+?\)/g, '$1') // Remove links
      .replace(/`(.+?)`/g, '$1') // Remove inline code
      .replace(/^[-*+]\s+/gm, '') // Remove list markers
      .replace(/^\d+\.\s+/gm, '') // Remove numbered lists
      .trim();
  }

  /**
   * Extract text from JSON content structure
   * @param {any | string} jsonContent - Docling JSON content
   * @returns {string} Extracted text
   */
  extractTextFromJsonContent(jsonContent: any | string): string {
    if (!jsonContent) return '';

    // If it's a string, parse it
    let content = jsonContent;
    if (typeof jsonContent === 'string') {
      try {
        content = JSON.parse(jsonContent);
      } catch (_error) {
        return jsonContent; // Return as-is if not valid JSON
      }
    }

    // Debug: Log JSON structure
    logger.debug('JSON content structure:', {
      hasMainText: !!content.main_text,
      hasBody: !!content.body,
      hasPages: !!content.pages,
      hasElements: !!content.elements,
      topLevelKeys: Object.keys(content).slice(0, 10),
    });

    // Extract text from various JSON structures
    const textParts: string[] = [];

    // Handle main_text or body
    if (content.main_text) {
      textParts.push(content.main_text);
    } else if (content.body) {
      textParts.push(this.extractTextFromElement(content.body));
    }

    // Handle texts array (DoclingDocument format)
    if (content.texts && Array.isArray(content.texts)) {
      content.texts.forEach((textItem: any) => {
        // Use 'text' property, fallback to 'orig' (original text)
        const text = textItem.text || textItem.orig;
        if (text && text.trim().length > 0) {
          textParts.push(text);
        }
      });
    }

    // Handle pages structure
    if (content.pages && Array.isArray(content.pages)) {
      content.pages.forEach((page: any) => {
        if (page.text) {
          textParts.push(page.text);
        } else if (page.content) {
          textParts.push(this.extractTextFromElement(page.content));
        }
      });
    }

    // Handle elements array
    if (content.elements && Array.isArray(content.elements)) {
      content.elements.forEach((element: any) => {
        textParts.push(this.extractTextFromElement(element));
      });
    }

    logger.debug('Extracted text parts count:', textParts.length);

    return textParts
      .filter((text) => text && text.trim().length > 0)
      .join('\n\n');
  }

  /**
   * Extract text from a document element (recursive)
   * @param {any | string} element - Document element
   * @returns {string} Extracted text
   */
  extractTextFromElement(element: any | string): string {
    if (!element) return '';

    if (typeof element === 'string') return element;

    const textParts: string[] = [];

    // Extract text property
    if (element.text) {
      textParts.push(element.text);
    }

    // Extract content property
    if (element.content) {
      if (typeof element.content === 'string') {
        textParts.push(element.content);
      } else if (Array.isArray(element.content)) {
        element.content.forEach((item: any) => {
          textParts.push(this.extractTextFromElement(item));
        });
      }
    }

    // Extract children
    if (element.children && Array.isArray(element.children)) {
      element.children.forEach((child: any) => {
        textParts.push(this.extractTextFromElement(child));
      });
    }

    return textParts.filter((text) => text && text.trim().length > 0).join(' ');
  }

  /**
   * Extract text from docling pages structure (legacy support)
   * @param {any[]} pages - Docling pages array
   * @returns {string} Extracted text
   */
  extractTextFromPages(pages: any[]): string {
    if (!Array.isArray(pages)) return '';

    return pages
      .map((page) => {
        if (page.text) return page.text;
        if (page.content) return page.content;
        return '';
      })
      .filter((text) => text.length > 0)
      .join('\n\n');
  }
}
